/**
 * Message Service
 * Pure functions for managing chat messages
 * Returns action objects instead of dispatching directly
 */

import type { Dispatch } from '@reduxjs/toolkit';

import { MESSAGE_SUBTYPES, MESSAGE_TYPES } from '../constants/steps';
import type { MessageSubType, StepFormMessage } from '../data-layer/types';
import { addMessage, setActiveStep } from '../data-store/copilotSlice';

import {
  createMessage,
  EDIT_WELCOME_MESSAGE,
  MESSAGE_TEMPLATES,
} from './messageTemplatesService';

/**
 * Create account helper message with 3 buttons
 */
export const createAccountHelperMessage = (subType: MessageSubType) => ({
  id: `account-helper-${Date.now()}`,
  type: 'ai_response' as const,
  subType,
  timestamp: Date.now(),
  buttons: [
    { label: 'Specify Prompt', action: 'all_account_help_yes' },
    { label: 'Upload Browser Recording', action: 'all_account_help_yes' },
    { label: 'Skip for Now', action: 'all_account_help_no' },
  ],
});

/**
 * Create welcome message action
 */
export const createWelcomeMessageAction = () => {
  const message = createMessage(MESSAGE_TEMPLATES.WELCOME);

  return addMessage(message);
};

/**
 * Create edit mode welcome message action
 */
export const createEditWelcomeMessageAction = () => {
  const message = createMessage({
    id: 'edit-welcome',
    type: MESSAGE_TYPES.AI_RESPONSE,
    subType: MESSAGE_SUBTYPES.START,
    text: EDIT_WELCOME_MESSAGE.WELCOME,
  });

  return addMessage(message);
};

/**
 * Create actions for showing form and setting active step
 */
export const createShowFormActions = (
  formMessage: StepFormMessage,
  stepName: string
) => ({
  showMessage: addMessage(formMessage),
  setStep: setActiveStep(stepName),
});

/**
 * Show success message with custom text
 */
export const showSuccess = (dispatch: Dispatch, text: string) => {
  dispatch(
    addMessage({
      id: `success-${Date.now()}`,
      type: 'ai_response',
      subType: 'success',
      text,
      timestamp: Date.now(),
    })
  );
};

/**
 * Show loading message with custom text
 */
export const showLoading = (dispatch: Dispatch, text: string) => {
  dispatch(
    addMessage({
      id: `loading-${Date.now()}`,
      type: 'ai_response',
      subType: 'analyzing',
      text,
      timestamp: Date.now(),
      loader: true,
    })
  );
};

/**
 * Show error message with custom text
 */
export const showError = (dispatch: Dispatch, text: string) => {
  dispatch(
    addMessage({
      id: `error-${Date.now()}`,
      type: 'ai_response',
      subType: 'error',
      text,
      timestamp: Date.now(),
    })
  );
};

/**
 * Add user response message
 */
export const addUserResponse = (
  dispatch: Dispatch,
  text: string,
  subType: MessageSubType = 'userResponseYes'
) => {
  dispatch(
    addMessage({
      id: Date.now().toString(),
      type: 'user_response',
      subType,
      text,
      timestamp: Date.now(),
    })
  );
};
